import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { useBusiness } from '../../../contexts/BusinessContext';
import api from '../../../services/api';

const AssignmentsTab = ({ formData, setFormData }) => {
  const { activeBusiness } = useBusiness();
  const [customers, setCustomers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [newArea, setNewArea] = useState('');

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchCustomers();
      fetchStores();
    }
  }, [activeBusiness]);

  const fetchStores = async () => {
    setLoadingStores(true);
    try {
      const { data } = await api.get('/stores');
      setStores(data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoadingStores(false);
    }
  };

  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const { data } = await api.get('/parties', { params: { type: 'customer' } });
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const toggleStore = (storeId) => {
    const current = formData.assignments?.stores || [];
    const updated = current.includes(storeId)
      ? current.filter(id => id !== storeId)
      : [...current, storeId];
    
    setFormData(prev => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        stores: updated
      }
    }));
  };

  const toggleCustomer = (customerId) => {
    const current = formData.assignments?.customers || [];
    const updated = current.includes(customerId)
      ? current.filter(id => id !== customerId)
      : [...current, customerId];
    
    setFormData(prev => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        customers: updated
      }
    }));
  };

  const addArea = () => {
    if (!newArea.trim()) return;
    
    const current = formData.assignments?.areas || [];
    if (!current.includes(newArea.trim())) {
      setFormData(prev => ({
        ...prev,
        assignments: {
          ...prev.assignments,
          areas: [...current, newArea.trim()]
        }
      }));
    }
    setNewArea('');
  };

  const removeArea = (area) => {
    const current = formData.assignments?.areas || [];
    setFormData(prev => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        areas: current.filter(a => a !== area)
      }
    }));
  };

  const selectAllCustomers = () => {
    setFormData(prev => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        customers: customers.map(c => c.id)
      }
    }));
  };

  const clearAllCustomers = () => {
    setFormData(prev => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        customers: []
      }
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Optional assignments:</strong> Restrict field staff access to specific stores, customers, or areas. 
          Leave empty to grant access to all.
        </p>
      </div>

      {/* Stores Assignment */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Assign Stores / Locations
        </label>
        
        <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
          {loadingStores ? (
            <div className="p-4 text-center text-sm text-gray-500">
              Loading stores...
            </div>
          ) : stores.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No stores found. <a href="/settings/stores" className="text-teal-600 hover:underline">Create stores</a> to assign them to field staff.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {stores.map(store => {
                const isSelected = formData.assignments?.stores?.includes(store.id);
                return (
                  <label
                    key={store.id}
                    className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleStore(store.id)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-900">{store.name}</p>
                      <p className="text-xs text-gray-500">{store.type} - {store.status}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Selected: {formData.assignments?.stores?.length || 0} / {stores.length}
        </p>
      </div>

      {/* Customer Assignment */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-700">
            Assign Customers
          </label>
          <div className="flex gap-2">
            <button
              onClick={selectAllCustomers}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium"
            >
              Select All
            </button>
            <button
              onClick={clearAllCustomers}
              className="text-xs text-gray-600 hover:text-gray-700 font-medium"
            >
              Clear All
            </button>
          </div>
        </div>
        
        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
          {loadingCustomers ? (
            <div className="p-4 text-center text-sm text-gray-500">
              Loading customers...
            </div>
          ) : customers.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No customers found. Add customers first.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {customers.map(customer => {
                const isSelected = formData.assignments?.customers?.includes(customer.id);
                return (
                  <label
                    key={customer.id}
                    className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleCustomer(customer.id)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-900">{customer.name}</p>
                      <p className="text-xs text-gray-500">{customer.mobile || customer.phone}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Selected: {formData.assignments?.customers?.length || 0} / {customers.length}
        </p>
      </div>

      {/* Area Assignment */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Assign Areas / Routes
        </label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newArea}
            onChange={(e) => setNewArea(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addArea()}
            placeholder="Enter area name (e.g., North Zone, Route A)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
          <button
            onClick={addArea}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {formData.assignments?.areas?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {formData.assignments.areas.map((area, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-100 text-teal-800 rounded-full text-sm"
              >
                <span>{area}</span>
                <button
                  onClick={() => removeArea(area)}
                  className="hover:bg-teal-200 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-sm text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
            No areas assigned. Field staff will have access to all areas.
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentsTab;
